import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer
} from 'recharts';

import { useCallData } from '../../contexts/CallDataContext';
import { useWeek } from '../../contexts/WeekContext';
import { format, addDays, isWithinInterval, startOfDay, endOfDay, differenceInDays } from 'date-fns';

import { parseCustomTimestamp } from '../../utils/dateUtils';

const MessagesChart = ({ clientId }) => {
    const { data } = useCallData();
    const { dateRange } = useWeek();

    // This function will now return both the chart data and the period average
    const processDataForChart = () => {
        if (!dateRange || !dateRange.start || !dateRange.end) {
            return { chartData: [], weeklyAverageMessages: '0.0' };
        }

        // Calculate the number of days in the selected range
        const dayCount = differenceInDays(dateRange.end, dateRange.start) + 1;
        
        // Generate dates for the selected range
        const rangeDates = Array.from({ length: dayCount }, (_, i) => 
            addDays(dateRange.start, i)
        );
        
        const dailyData = rangeDates.reduce((acc, date) => {
            acc[format(date, 'MM/dd')] = {
                totalMessages: 0,
                callCount: 0
            };
            return acc;
        }, {});

        let periodTotalMessages = 0;
        let periodTotalCalls = 0;

        // Sum up messages and calls for each day
        data.forEach(call => {
            const callDate = parseCustomTimestamp(call?.metadata?.timestamp);
            if (callDate) {
                const rangeStart = startOfDay(dateRange.start);
                const rangeEnd = endOfDay(dateRange.end);
                
                if (isWithinInterval(callDate, { start: rangeStart, end: rangeEnd })) {
                    const messageCount = call.analytics.message_count || 0;

                    // Add to daily totals
                    const formattedDate = format(callDate, 'MM/dd', { timeZone: 'UTC' });
                    if (dailyData.hasOwnProperty(formattedDate)) {
                        dailyData[formattedDate].totalMessages += messageCount;
                        dailyData[formattedDate].callCount++;
                    }

                    // Add to period totals for the top display calculation
                    periodTotalMessages += messageCount;
                    periodTotalCalls++;
                }
            }
        });

        // Calculate average messages PER DAY for the chart
        const chartData = rangeDates.map(date => {
            const formattedDate = format(date, 'MM/dd');
            const dayData = dailyData[formattedDate];
            const avgMessages = dayData.callCount > 0 
                ? +(dayData.totalMessages / dayData.callCount).toFixed(1)
                : 0;
            return {
                date: formattedDate,
                messages: avgMessages
            };
        });
        
        // Calculate overall average messages PER CALL for the period
        const weeklyAverageMessages = periodTotalCalls > 0 
            ? (periodTotalMessages / periodTotalCalls).toFixed(1) 
            : '0.0';

        return { chartData, weeklyAverageMessages };
    };

    const { chartData, weeklyAverageMessages } = processDataForChart();

    return (
        <div className="flex flex-col items-center card-container w-full h-[300px] space-y-2">
            <div className='flex flex-row w-full items-center justify-between'>
                <div className="text-sm text-font-primary mb-2">
                    {clientId === 'kronk' ? 'Average Messages per Chat' : 'Average Messages per Call'}
                </div>
                <span className='text-lg font-semibold'>{weeklyAverageMessages}</span>
            </div>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 10, left: -30, bottom: -5 }}>
                    <XAxis dataKey="date" stroke="#aaa" />
                    <YAxis stroke="#aaa" />
                    <Tooltip />
                    <Line
                        type="monotone"
                        dataKey="messages"
                        stroke="#db3069"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

export default MessagesChart;
  
